import { FaEraser, FaPlus, FaSearch } from "react-icons/fa";
import Button from "../../../../components/button/button";
import { Template } from "../../../../components/template/template";
import { navigateHook } from "../../../../hooks/use-navigate";
import { Card } from "../../../../components/card/card";
import InputForm from "../../../../components/input/input-form";
import {useCallback, useEffect, useMemo, useRef, useState} from "react";
import { useQuery } from "@tanstack/react-query";
import ReviewItemsService from "../../../../services/web/review-items-service";
import { AgGridReact } from "ag-grid-react";
import AgGridUtils from "../../../../utils/aggrid-utils";
import {DEFAULT_COL_DEF_NO_RESIZE_SUPPRESS_ALL} from "../../../../utils/ag-grid-columns-utils.js";

const initialFilter = {
    description: "",
}

export default function ReviewItems() {
    const navigate = navigateHook();
    const [filter, setFilter] = useState(initialFilter);
    const [gridApi, setGridApi] = useState(null);
    const gridRef = useRef(null);

    const { data: review, isLoading } = useQuery({
        queryKey: ['review-items'],
        queryFn: () => ReviewItemsService.list(),
        initialData: []
    });

    const columnDefs = useMemo(() => ([
        {headerName: "Nome", field: "description", flex: 1, sortable: true, comparator: AgGridUtils.lowerUpCaseComparator, filter: "agTextColumnFilter", filterParams: AgGridUtils.filterParams},
    ]), []);

    const handleClear = () => {
        setFilter(initialFilter);
        const api = gridRef.current?.api;
        if (api) {
            api.setFilterModel(null);
            api.onFilterChanged();
        }
    }

    const handleSearch = useCallback(() => {
        if (!gridApi) return;

        const filterModel = {};

        Object.entries(filter).forEach(([field, value]) => {
            if (!value) return;
            filterModel[field] = {
                filterType: "text",
                type: "contains",
                filter: value
            }
        });

        gridApi.setFilterModel(filterModel);
        gridApi.onFilterChanged();
    }, [filter, gridApi]);

    const handleKeyDown = (e) => {
        if (e.key === "Enter") handleSearch();
        if (e.key === "Escape") handleClear();
    };

    const onCellClicked = ({ data, event }) => {
        if (!data?.id) return;

        const urlDev = `/review-items/form/${data.id}`;
        
        navigate(urlDev, event.ctrlKey);
    };

    const newReviewItem = () => {
        const urlDev = "/review-items/form";
        navigate(urlDev);
    };

    return (
        <Template isLoading={isLoading}>
            <Template.Header>
                <Template.Title>Itens de revisão</Template.Title>
                <Button onClick={newReviewItem}><FaPlus />Adicionar item de revisão</Button>
            </Template.Header>
            <Template.Body className="space-y-2 flex flex-col">
                <Card>
                    <Card.Body>
                        <div className="flex gap-4 items-end">
                            <InputForm
                                id="description"
                                label="Nome"
                                value={filter.description}
                                onChange={({target: {id, value}}) => setFilter(prev => ({ ...prev, [id]: value }))}
                                onKeyDown={handleKeyDown}
                                classNameWrapper="flex-1"
                            />
                            <div className="flex gap-2">
                                <Button 
                                    target="primary-outline"
                                    title="Limpar"
                                    onClick={handleClear}
                                >
                                    <FaEraser />
                                </Button>
                                <Button
                                    title="Buscar"
                                    onClick={handleSearch}
                                >
                                    <FaSearch />
                                </Button>
                            </div>
                        </div>
                    </Card.Body>
                </Card>
                <Card>
                    <Card.Body className="flex">
                        <div className="h-[calc(100vh-153px)] w-full">
                            <AgGridReact
                                onGridReady={(params) => {
                                    setGridApi(params.api);
                                }}
                                onFilterChanged={AgGridUtils.filterChangedNoRowsOverlay}
                                ref={gridRef}
                                rowData={review}
                                columnDefs={columnDefs}
                                defaultColDef={DEFAULT_COL_DEF_NO_RESIZE_SUPPRESS_ALL}
                                pagination
                                paginationPageSize={100}
                                paginationPageSizeSelector={[20, 50, 100, 500]}
                                animateRows={false}
                                suppressCellFocus
                                onCellClicked={onCellClicked}
                                gridOptions={{localeText: AgGridUtils.localization()}}
                                theme={AgGridUtils.generalTheme()}
                            />
                        </div>
                    </Card.Body>
                </Card>
            </Template.Body>
        </Template>
    );   
};